import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

// Release 1.5.0 (perf, behind the 1.5.0 switch) — conditional GETs. The API tags every JSON answer with a weak ETag and
// `no-store` (so the browser never writes it to disk). This keeps the last tagged answer of each address IN MEMORY for
// the current sign-in only, sends its tag back as If-None-Match, and turns the API's empty 304 into that same answer.
// An unchanged screen on the background refresh then costs a few hundred bytes instead of the whole payload.

const MAX_ENTRIES = 80;
const EXCLUDED = ["/search", "/data/freshness", "/me", "/hubs", "/live", "/admin", "/auth"];

interface Entry {
  etag: string;
  data: unknown;
}

const entries = new Map<string, Entry>();

type ConditionalConfig = InternalAxiosRequestConfig & { _conditionalKey?: string; _conditionalSent?: boolean };

/** The API path of a request (without the /api/v1 base) — used for the exclusion list. */
function apiPath(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/i, "").split("?")[0] ?? "";
  return path.replace(/^\/api\/v1/i, "");
}

export function isConditionalCandidate(method: string | undefined, url: string): boolean {
  if ((method ?? "get").toLowerCase() !== "get") return false;
  const path = apiPath(url);
  return !EXCLUDED.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Forget every kept answer (sign-out, a different person or a role change). */
export function clearConditionalCache(): void {
  entries.clear();
}

export function conditionalCacheSize(): number {
  return entries.size;
}

/** Installs the interceptors on the API client. Returns the uninstall function. */
export function installConditionalGet(client: AxiosInstance): () => void {
  const request = client.interceptors.request.use((config: ConditionalConfig) => {
    const url = client.getUri(config);
    if (!isConditionalCandidate(config.method, url)) return config;
    config._conditionalKey = url;
    const kept = entries.get(url);
    if (kept && !config.headers.has("If-None-Match")) {
      config.headers.set("If-None-Match", kept.etag);
      config._conditionalSent = true;
      const previous = config.validateStatus;
      config.validateStatus = (status) => status === 304 || (previous ? previous(status) : status >= 200 && status < 300);
    }
    return config;
  });

  const response = client.interceptors.response.use(async (res: AxiosResponse) => {
    const config = res.config as ConditionalConfig;
    const key = config._conditionalKey;
    if (!key) return res;
    if (res.status === 304) {
      const kept = entries.get(key);
      if (kept) {
        // Refresh its place in the LRU order.
        entries.delete(key);
        entries.set(key, kept);
        return { ...res, status: 200, statusText: "OK", data: kept.data };
      }
      // Forgotten meanwhile (sign-out, eviction): ask again without the tag.
      const retry = { ...config, _conditionalSent: false } as ConditionalConfig;
      retry.headers.delete("If-None-Match");
      return client.request(retry);
    }
    const etag = res.headers?.["etag"] as string | undefined;
    if (res.status === 200 && typeof etag === "string" && etag.length > 0) {
      entries.delete(key);
      entries.set(key, { etag, data: res.data });
      while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value as string);
    }
    return res;
  });

  return () => {
    client.interceptors.request.eject(request);
    client.interceptors.response.eject(response);
    clearConditionalCache();
  };
}
